import React, { useState } from 'react';
import { api, blankWindow } from '../lib/api.js';
import { compact, day, qty } from '../lib/format.js';
import { parseNumber } from '../lib/numbers.js';
import { useEntityQuery } from '../lib/entity.js';
import { useSession } from '../App.jsx';
import ScanBar from '../components/ScanBar.jsx';
import { Alert, Card, CardSkeleton, Empty, Field, LoadMore, Pill, useAsync, usePagedList } from '../components/ui.jsx';

/**
 * The godown's whole screen: the orders waiting, and the one action that clears
 * them. Batches are proposed first-expiry-first-out; the operator adjusts what
 * actually went out and confirms. Large targets — this runs on a shared terminal.
 */
export default function GodownQueue() {
  const entityQuery = useEntityQuery();
  const queue = usePagedList(`/api/delivery-orders?status=issued,partially_delivered&${entityQuery}`, 'delivery_orders');
  const [active, setActive] = useState(null);

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Godown queue</h1>
          <div className="page-sub">
            Orders waiting to be given away. Confirming records the batches that physically went out — that is what
            carries the chemical properties onto the invoice.
          </div>
        </div>
      </div>

      <Alert onRetry={queue.reload}>{queue.error}</Alert>
      {queue.loading && !queue.data && <CardSkeleton lines={3} />}

      {queue.data && queue.data.delivery_orders.length === 0 && (
        <Card><Empty title="Nothing pending">Every issued order has been despatched in full.</Empty></Card>
      )}

      {queue.data && queue.data.delivery_orders.map((o) => (
        <Card key={o.id}
          title={`${o.do_number} · ${o.buyer_name}`}
          note={`${o.godown_name} · raised ${day(o.do_date)} · ${compact(o.qty_pending)} units pending`}
          actions={(
            <>
              <Pill value={o.status} />
              <button className="btn" onClick={() => setActive(active === o.id ? null : o.id)}>
                {active === o.id ? 'Close' : 'Prepare despatch'}
              </button>
            </>
          )}>
          {active === o.id && <Despatch orderId={o.id} onDone={() => { setActive(null); queue.reload(); }} />}
        </Card>
      ))}
      {queue.data && <LoadMore list={queue} />}
    </>
  );
}

function Despatch({ orderId, onDone }) {
  const session = useSession();
  const order = useAsync(() => api.get(`/api/delivery-orders/${orderId}`), [orderId]);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ vehicle_no: '', driver_name: '', received_by: '', strategy: 'fefo' });
  // Confirm despatches what the server has stored, so an editor with unsaved
  // changes would otherwise send out batches the operator just took off.
  const [dirtyLines, setDirtyLines] = useState({});
  const unsaved = Object.values(dirtyLines).some(Boolean);

  const create = async () => {
    setBusy(true); setError(null);
    try { setDraft(await api.post('/api/deliveries', { delivery_order_id: orderId, ...form })); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true); setError(null);
    // Opened now, while the click still counts, so the challan is not taken for a pop-up.
    const win = blankWindow();
    try {
      const confirmed = await api.post(`/api/deliveries/${draft.id}/confirm`);
      if (confirmed.spec_findings && confirmed.spec_findings.length) {
        // Not a failure — the despatch stands, but somebody should know.
        window.alert(`Despatched, but note:\n\n${confirmed.spec_findings
          .map((f) => `${f.batch}: ${f.parameter} ${f.reason} (needs ${f.required}, is ${f.actual ?? 'not recorded'})`)
          .join('\n')}`);
      }
      // The gate needs the paper, so it opens as part of confirming.
      api.open(`/api/deliveries/${draft.id}/challan?format=pdf`, win)
        .catch((err) => window.alert(`Despatched, but the challan could not be opened: ${err.message}`));
      onDone();
    }
    catch (e) {
      if (win) win.close();
      if (e.details && e.details.shortages) {
        setError(`${e.message}: ${e.details.shortages.map((s) => `${s.batch} has ${s.available}, needs ${s.requested}`).join('; ')}`);
      } else if (e.details && e.details.spec_findings) {
        setError(`${e.message}: ${e.details.spec_findings
          .map((f) => `${f.batch} — ${f.parameter} ${f.reason} (needs ${f.required}, is ${f.actual ?? 'not recorded'})`)
          .join('; ')}`);
      } else {
        setError(e.message);
      }
    } finally { setBusy(false); }
  };

  if (order.loading && !order.data) return <CardSkeleton lines={2} />;
  if (order.error) return <Alert onRetry={order.reload}>{order.error}</Alert>;
  if (!order.data) return null;

  // Scanning is the company's choice (Settings): off, offered, or required.
  const entity = (session.allEntities || session.entities).find((e) => e.id === order.data.entity_id);
  const scanMode = (entity && entity.godown_scan_mode) || 'off';
  const scans = Object.fromEntries(((draft && draft.scans) || []).map((s) => [s.batch_id, s.scans]));
  const unscanned = draft ? draft.lines.flatMap((l) => l.allocations)
    .filter((a) => Number(a.qty) > 0 && !scans[a.batch_id]).map((a) => a.batch_name) : [];
  const scanBlocked = scanMode === 'required' && unscanned.length > 0;

  const scan = async (code) => {
    try {
      const r = await api.post(`/api/deliveries/${draft.id}/scan`, { code });
      setDraft(r.delivery);
      const n = (r.delivery.scans.find((s) => s.batch_id === r.scanned.batch_id) || {}).scans || 1;
      return { ok: true, message: `${r.scanned.batch_name} · ${r.scanned.item_name}${n > 1 ? ` · scanned ${n} times` : ''}` };
    } catch (e) { return { ok: false, message: e.message }; }
  };

  return (
    <div style={{ borderTop: '1px solid var(--line)', paddingTop: 16, marginTop: 4 }}>
      <Alert>{error}</Alert>

      {!draft && (
        <>
          <div className="table-wrap" style={{ marginBottom: 16 }}>
            <table>
              <thead><tr><th>Item</th><th className="r">Ordered</th><th className="r">Already out</th><th className="r">To despatch</th></tr></thead>
              <tbody>
                {order.data.lines.map((l) => (
                  <tr key={l.id}>
                    <td>{l.item_name}</td>
                    <td className="r num">{qty(l.qty, l.uom)}</td>
                    <td className="r num">{qty(l.qty_delivered)}</td>
                    <td className="r num"><b>{qty(l.qty_pending)}</b></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="row">
            <Field label="Vehicle"><input value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })} placeholder="WB 23 A 4411" /></Field>
            <Field label="Driver"><input value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} /></Field>
            <Field label="Received by"><input value={form.received_by} onChange={(e) => setForm({ ...form, received_by: e.target.value })} /></Field>
            <Field label="Batch selection" hint="you can change any of it">
              <select value={form.strategy} onChange={(e) => setForm({ ...form, strategy: e.target.value })}>
                <option value="fefo">First expiry, first out</option>
                <option value="fifo">Oldest purchase first</option>
              </select>
            </Field>
          </div>
          <button className="btn big" disabled={busy} onClick={create}>
            {busy ? 'Working…' : 'Propose batches for the pending quantity'}
          </button>
        </>
      )}

      {draft && (
        <>
          {draft.warnings && draft.warnings.length > 0 && (
            <Alert kind="warn">{draft.warnings.join(' · ')}</Alert>
          )}
          {scanMode !== 'off' && (
            <Card title="Scan as you load"
              note={scanMode === 'required'
                ? 'Every batch going out must be scanned before the despatch can be confirmed.'
                : 'Scanning checks each drum against this despatch. It is optional here.'}>
              <ScanBar onCode={scan} />
              {unscanned.length > 0 && (
                <div className="card-note" style={{ marginTop: 8 }}>Not scanned yet: {unscanned.join(', ')}</div>
              )}
            </Card>
          )}
          {draft.lines.map((line) => (
            <AllocationEditor key={line.id} deliveryId={draft.id} godownId={draft.godown_id || order.data.godown_id} line={line}
              scans={scanMode !== 'off' ? scans : null}
              onSaved={setDraft} setError={setError}
              onDirty={(d) => setDirtyLines((prev) => (prev[line.id] === d ? prev : { ...prev, [line.id]: d }))} />
          ))}
          <div className="inline" style={{ marginTop: 14 }}>
            <button className="btn big" disabled={busy || unsaved || scanBlocked} onClick={confirm}
              title={unsaved ? 'Save the allocation changes first' : scanBlocked ? 'Scan every batch first' : undefined}>
              Confirm despatch &amp; print challan
            </button>
            <button className="btn secondary" disabled={busy}
              onClick={async () => {
                setBusy(true); setError(null);
                try {
                  await api.post(`/api/deliveries/${draft.id}/cancel`, { reason: 'Abandoned at the godown' });
                  onDone();
                } catch (e) { setError(e.message); setBusy(false); }
              }}>
              Discard
            </button>
            {unsaved
              ? <span className="card-note" style={{ color: 'var(--critical-text)' }}>Save the allocation changes above before confirming.</span>
              : <span className="card-note">Confirming updates the order and readies the invoice.</span>}
          </div>
        </>
      )}
    </div>
  );
}

// Serials are kept as typed and split only on save; splitting on every
// keystroke swallowed the comma before the next number could be typed.
const serialsText = (a) => (a.package_serials ? a.package_serials.join(', ') : '');
const splitSerials = (text) => String(text || '').split(',').map((v) => v.trim()).filter(Boolean);

function AllocationEditor({ deliveryId, godownId, line, onSaved, setError, onDirty, scans = null }) {
  const [rows, setRows] = useState(line.allocations.map((a) => ({ ...a, qty: Number(a.qty), serials: serialsText(a) })));
  const [dirty, setDirty] = useState(false);
  const [available, setAvailable] = useState(null);
  const [saving, setSaving] = useState(false);
  const parsed = rows.map((r) => parseNumber(r.qty));
  const invalid = parsed.some((n) => Number.isNaN(n) || (n !== null && n < 0));
  const total = parsed.reduce((a, n) => a + (Number.isFinite(n) ? n : 0), 0);
  const target = Number(line.qty);
  const matches = !invalid && Math.abs(total - target) < 1e-6;

  React.useEffect(() => { if (onDirty) onDirty(dirty); }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps
  const edit = (next) => { setRows(next); setDirty(true); };

  // Only this despatch's godown: stock elsewhere cannot go out on this challan.
  const loadAvailable = async () => {
    setError(null);
    try {
      const res = await api.get(`/api/batches?item_id=${line.item_id}&in_stock=true${godownId ? `&godown_id=${godownId}` : ''}`);
      setAvailable(res.batches);
    } catch (e) { setError(e.message); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.put(`/api/deliveries/${deliveryId}/lines/${line.id}/allocations`, {
        allocations: rows.map((r, i) => ({ batch_id: r.batch_id, qty: parsed[i] }))
          .filter((r) => r.qty > 0),
      });
      const packed = rows.map((r) => ({ batch_id: r.batch_id, package_serials: splitSerials(r.serials) }))
        .filter((r) => r.package_serials.length);
      if (packed.length) {
        await api.put(`/api/deliveries/${deliveryId}/lines/${line.id}/packing`, { allocations: packed });
      }
      setDirty(false);
      onSaved(updated);
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="card" style={{ background: 'var(--surface-2)', marginTop: 14 }}>
      <div className="card-head">
        <div>
          <h2 className="card-title">{line.item_name}</h2>
          <div className="card-note">{qty(line.qty, line.uom)} to go out</div>
        </div>
        <div className="inline">
          <button className="btn small secondary" onClick={loadAvailable}>Add another batch</button>
          <button className="btn small" disabled={!matches || saving} onClick={save}>
            {saving ? 'Saving…' : 'Save allocation'}
          </button>
        </div>
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr><th>Batch</th><th>Expiry</th><th>Properties</th>
          <th className="r" style={{ width: 150 }}>Quantity</th>
          <th style={{ width: 210 }}>Drum / pack numbers</th>{scans && <th>Scanned</th>}<th /></tr></thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={r.batch_id}>
                <td className="mono">{r.batch_name}</td>
                <td>{day(r.expiry_date)}</td>
                <td><Pill value={r.spec_status} /></td>
                <td className="r">
                  <input className="num" style={{ textAlign: 'right' }} value={r.qty} inputMode="decimal"
                    onChange={(e) => edit(rows.map((x, i) => (i === idx ? { ...x, qty: e.target.value } : x)))} />
                </td>
                <td>
                  <input value={r.serials}
                    placeholder="DRM-4471, DRM-4472"
                    onChange={(e) => edit(rows.map((x, i) => (i === idx ? { ...x, serials: e.target.value } : x)))} />
                </td>
                {scans && (
                  <td>{scans[r.batch_id]
                    ? <span className="pill good">✓ {scans[r.batch_id] > 1 ? `${scans[r.batch_id]}×` : 'yes'}</span>
                    : <span className="pill">not yet</span>}</td>
                )}
                <td className="r">
                  <button className="btn small ghost" aria-label={`Remove ${r.batch_name}`}
                    onClick={() => edit(rows.filter((_, i) => i !== idx))}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan={3} className="r">Allocated</th>
              <th className="r num" style={{ color: matches ? 'var(--good-text)' : 'var(--critical-text)' }}>
                {qty(total)} of {qty(target)}
              </th>
              <th />
            </tr>
          </tfoot>
        </table>
      </div>

      {!matches && (
        <div className="card-note" style={{ marginTop: 8, color: 'var(--critical-text)' }}>
          {invalid
            ? 'Every quantity must be a number.'
            : 'The allocation must add up to the line quantity before it can be saved.'}
        </div>
      )}

      {available && (
        <div style={{ marginTop: 12 }}>
          <div className="label" style={{ marginBottom: 6 }}>Available at this godown</div>
          <div className="inline">
            {available.map((b) => (
              <button key={b.id} className="btn small secondary" disabled={rows.some((r) => r.batch_id === b.id)}
                onClick={() => edit([...rows, {
                  batch_id: b.id, batch_name: b.batch_name, expiry_date: b.expiry_date, spec_status: b.spec_status, qty: 0, serials: '',
                }])}>
                {b.batch_name} · {qty(b.qty_available)} free{b.spec_status === 'pending' ? ' · no properties' : ''}
              </button>
            ))}
            {available.length === 0 && <span className="muted">No other batch of this item has stock here.</span>}
          </div>
        </div>
      )}
    </div>
  );
}
